import {
  BMComponentsType,
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcPaymentCreditCard from "@traveloka/icon-kit-web/react/IcPaymentCreditCard";
import { Pressable, StyleSheet } from "react-native";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { CurrencyValue } from "../../../utils";
import { BalanceStatus } from "./types";
import { createBalanceIndicatorSpecs } from "./utils";

export type Props = {
  data: {
    status: BalanceStatus;
    amount?: CurrencyValue;
  };
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export default function BalanceWidget({
  data,
  isError,
  isFetching,
  refetch,
}: Props) {
  const { status, amount } = data;

  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  const {
    CorporateNavigationProfileWidget: cr,
    CorporateEnumCreditLimit: cre,
  } = useMobileContentResource();

  const balanceSpecs = createBalanceIndicatorSpecs(
    status,
    amount,
    cre,
    BMComponents
  );

  return (
    <BMView flex>
      <Pressable onPress={refetch} disabled={isFetching || !isError}>
        <BMView spacing="xs">
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {cr.companyBalanceLabel}
          </BMText>
          <BMView row align="center" spacing="xs">
            <BMView>
              <IcPaymentCreditCard
                width={16}
                height={16}
                color={BMComponents.BMStyleIconInkPrimary}
                accentColor={BMComponents.BMStyleIconInkPrimary}
              />
              <BMView
                style={[
                  styles.balanceIndicator,
                  {
                    backgroundColor:
                      !isFetching && !isError
                        ? balanceSpecs.color
                        : BMComponents.BMStyleTextInkSecondary,
                  },
                ]}
              />
            </BMView>
            {isFetching && (
              <BMSkeletonLoader
                width={BMPrimitives.BMSize.xl6}
                height={BMPrimitives.BMSize.lg}
              />
            )}
            {!isFetching && isError && (
              <BMView row spacing="xs">
                <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
                  {cr.failedToFetchData}
                </BMText>
                <BMText variant="uiSmallLi" ink="BMStyleTextInkAction">
                  {cr.ctaTryAgain}
                </BMText>
              </BMView>
            )}
            {!isFetching && !isError && (
              <BMText variant="uiNormal" numberOfLines={1} ellipsizeMode="tail">
                {balanceSpecs.text}
              </BMText>
            )}
          </BMView>
        </BMView>
      </Pressable>
    </BMView>
  );
}

const createStyles = (BMComponents: BMComponentsType) => {
  return StyleSheet.create({
    balanceIndicator: {
      width: BMPrimitives.BMSize.sm,
      height: BMPrimitives.BMSize.sm,
      borderRadius: BMPrimitives.BMRadius.rounded,
      right: -1,
      bottom: 2,
      position: "absolute",
      backgroundColor: BMComponents.BMStyleTextInkPrimary,
    },
  });
};
